import { Wand2, UserCheck, Cpu } from "lucide-react";

export default function WhyDifferent() {
    return (
        <section
            id="why-different"
            className="relative px-6 md:px-10 py-16 md:py-24 bg-[#F1E8D5]"
            data-testid="why-different-section"
        >
            <div className="max-w-7xl mx-auto">
                <div className="reveal max-w-4xl mb-16 md:mb-20">
                    <h2 className="font-display tracking-tighter font-black leading-[0.95] text-5xl md:text-6xl lg:text-7xl text-black">
                        Not just lessons.
                        <br />
                        Not just a studio.
                        <br />
                        <span className="bg-[#1B4D3E] text-white px-3 -rotate-1 inline-block">A complete experience.</span>
                    </h2>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
                    {/* Big card — yellow */}
                    <div
                        className="md:col-span-3 md:row-span-2 bg-[#D4A017] text-black rounded-3xl p-8 md:p-10 relative overflow-hidden reveal border-2 border-black shadow-[8px_8px_0_0_#0A0A0A]"
                        data-testid="why-card-anchor"
                    >
                        <h3 className="font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter">
                            Built for people who never knew where to start.
                        </h3>
                        <p className="mt-6 text-black/80 text-base md:text-lg max-w-md">
                            We combine human creativity, professional music
                            production and advanced music production technology
                            to make creating real, released music more
                            accessible than ever before.
                        </p>
                        <div className="absolute -bottom-12 -right-12 w-56 h-56 rounded-full border-[10px] border-black/15" aria-hidden />
                        <div className="absolute -bottom-2 -right-2 w-24 h-24 rounded-full bg-[#C2410C]" aria-hidden />
                    </div>

                    {/* Three feature cards */}
                    <FeatureCard
                        icon={UserCheck}
                        title="Real producers, real coaches"
                        body="Every session is led by experienced humans who care about your story."
                        bg="bg-white"
                        accent="#C2410C"
                        className="md:col-span-3"
                    />
                    <FeatureCard
                        icon={Cpu}
                        title="Modern tools, on your side"
                        body="Custom beats, arrangement help, and production supported by advanced music production technology."
                        bg="bg-[#7C2D12]"
                        accent="#0A0A0A"
                        textColor="text-white"
                        className="md:col-span-2"
                    />
                    <FeatureCard
                        icon={Wand2}
                        title="Polished pro standard"
                        body="Mixing, mastering, artwork and worldwide distribution — all available."
                        bg="bg-white"
                        accent="#1B4D3E"
                        className="md:col-span-1"
                    />
                </div>

                <p className="mt-14 text-stone-700 text-lg max-w-2xl reveal">
                    This isn&rsquo;t about becoming famous overnight. It&rsquo;s about
                    expressing yourself, building confidence, creating
                    something real, and finally sharing your music with the
                    world.
                </p>
            </div>
        </section>
    );
}

function FeatureCard({ icon: Icon, title, body, bg = "bg-white", accent = "#C2410C", textColor = "text-black", className = "" }) {
    return (
        <div
            className={`${bg} ${textColor} rounded-3xl p-7 md:p-8 reveal border-2 border-black shadow-[5px_5px_0_0_#0A0A0A] hover:shadow-[2px_2px_0_0_#0A0A0A] hover:translate-x-[3px] hover:translate-y-[3px] transition-all ${className}`}
        >
            <span
                className="inline-flex w-10 h-10 rounded-xl items-center justify-center border-2 border-black mb-6"
                style={{ background: accent }}
            >
                <Icon size={18} className={accent === "#0A0A0A" ? "text-[#D4A017]" : "text-white"} />
            </span>
            <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">
                {title}
            </h3>
            <p className={`mt-4 text-sm md:text-base leading-relaxed ${textColor === "text-white" ? "text-white/85" : "text-stone-700"}`}>
                {body}
            </p>
        </div>
    );
}
